/**
 * Client-side email checking for the registration forms.
 *
 * `error` blocks the wizard's Next button; `suggestion` is advisory and never
 * blocks — someone may genuinely own a domain close to a common one, and a
 * wrong guess must not be able to stop them registering.
 *
 * These rules are deliberately stricter than the server's. Supabase GoTrue
 * rejects `not-an-email` but accepts `ada@localhost` (no TLD required), and it
 * remains the authority on what gets stored. This is the fast, specific half.
 */
export interface EmailCheck {
  error: string | null;
  suggestion: string | null;
}

/** Unquoted local-part characters permitted by RFC 5322 §3.2.3 (atext + dot). */
const LOCAL_ALLOWED = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+$/;

/**
 * Structural faults, most fundamental first — the first match wins so the
 * message names the root problem instead of a downstream symptom. Order matters
 * where two rules can both fire: the whitespace check precedes everything so a
 * quoted local part is reported as spaces, and the double-dot check precedes the
 * stray-edge check so `ada@.example..com` names the doubled dot.
 */
/** Consumer providers common enough that a near-miss is almost always a typo. */
const COMMON_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
  'icloud.com', 'aol.com', 'live.com', 'msn.com',
  'comcast.net', 'verizon.net', 'att.net', 'sbcglobal.net',
  'me.com', 'mac.com', 'proton.me', 'protonmail.com',
];

/**
 * Mistyped endings. `co` -> `com` is deliberate despite `.co` being a real TLD:
 * on a US consumer signup a dropped `m` is far likelier than a Colombian
 * domain, and the suggestion is advisory, so being wrong costs one ignored line.
 */
const TLD_SLIPS: Record<string, string> = {
  con: 'com', cmo: 'com', ocm: 'com', vom: 'com', xom: 'com', comm: 'com', co: 'com',
  ogr: 'org', or: 'org',
  nte: 'net', ner: 'net',
};

/** Iterative Levenshtein distance over a single rolling row. */
function distance(a: string, b: string): number {
  const row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let diag = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const above = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, diag + (a[i - 1] === b[j - 1] ? 0 : 1));
      diag = above;
    }
  }
  return row[b.length];
}

/**
 * Real mail domains close enough to a common provider to be mistaken for typos.
 * `ymail.com` (Yahoo's official alternate) is distance 1 from `gmail.com` and is
 * structurally indistinguishable from a typo — only membership here tells them
 * apart. Suggesting a replacement for one of these would hand the user someone
 * else's address, so they are never rewritten.
 */
const KNOWN_REAL_DOMAINS = new Set([
  'ymail.com', 'mail.com', 'email.com', 'onmail.com', 'mailo.com',
  'ao.com', 'msc.com', 'max.com', 'line.com',
]);

/** The part after the last dot. */
function tld(domain: string): string {
  return domain.slice(domain.lastIndexOf('.') + 1);
}

/**
 * Nearest common provider, or null. The threshold scales with the candidate's
 * length so short domains can't drag in unrelated ones. An exact match returns
 * null immediately — there is nothing to suggest.
 */
function nearestDomain(domain: string): string | null {
  if (KNOWN_REAL_DOMAINS.has(domain)) return null;
  const typedTld = tld(domain);
  let best: string | null = null;
  let bestDistance = Infinity;
  for (const candidate of COMMON_DOMAINS) {
    if (tld(candidate) !== typedTld) continue; // never swap one real TLD for another
    const d = distance(domain, candidate);
    if (d === 0) return null;
    const limit = candidate.length < 8 ? 1 : 2;
    if (d <= limit && d < bestDistance) {
      best = candidate;
      bestDistance = d;
    }
  }
  return best;
}

/** Domain near-miss first; a TLD slip is only consulted if that found nothing. */
function suggestDomain(domain: string): string | null {
  const near = nearestDomain(domain);
  if (near) return near;
  const dot = domain.lastIndexOf('.');
  const fixed = TLD_SLIPS[domain.slice(dot + 1)];
  if (!fixed) return null;
  const repaired = `${domain.slice(0, dot + 1)}${fixed}`;
  return nearestDomain(repaired) ?? repaired;
}

function structuralError(email: string): string | null {
  if (!email) return null; // emptiness is the step's own 'Required' check
  if (/\s/.test(email)) return "Email addresses can't contain spaces — remove the space.";

  const parts = email.split('@');
  if (parts.length === 1) return 'Add an @ — for example ada@example.com';
  if (parts.length > 2) return "There's more than one @ — an address needs exactly one.";

  const [local, domain] = parts;
  if (!local) return 'Add the part before the @ — for example ada@example.com';
  if (!domain) return 'Add a domain after the @ — for example gmail.com';
  if (domain.includes('..')) return 'The domain has two dots in a row.';
  if (/^[.-]|[.-]$/.test(domain)) return 'The domain starts or ends with a stray dot or dash — remove it.';
  if (!domain.includes('.')) return 'Add the domain ending — for example .com';

  const tld = domain.slice(domain.lastIndexOf('.') + 1);
  if (tld.length < 2 || !/^[A-Za-z]+$/.test(tld)) {
    return 'The domain ending looks wrong — for example .com or .org';
  }
  if (!LOCAL_ALLOWED.test(local)) return "The part before the @ has a character that isn't allowed.";
  if (local.includes('..')) return 'The part before the @ has two dots in a row.';
  if (/^\.|\.$/.test(local)) return 'The part before the @ starts or ends with a dot — remove it.';
  return null;
}

export function checkEmail(raw: string): EmailCheck {
  const email = raw.trim();
  const error = structuralError(email);
  if (error || !email) return { error, suggestion: null };

  const at = email.indexOf('@');
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const fixed = suggestDomain(domain.toLowerCase());
  return { error: null, suggestion: fixed ? `${local}@${fixed}` : null };
}
